import type { MailboxInfo } from 'owa-client-types';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { isAccountFeatureEnabled, isFeatureEnabled } from 'owa-feature-flags';
import { isLocalDataBackedMailboxInfo } from 'owa-account-source-list/lib/utils/isLocalDataBackedAccount';
import { getAccountScopeUserSettings } from 'owa-session-store';
import type OutlookDataFileProperty from 'owa-service/lib/contract/OutlookDataFileProperty';
import { isGulpOrBranching } from 'owa-config';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { getWin32Policy } from 'owa-session-store/lib/utils/win32Policy';
import { type MoveCopyType } from './copyMoveItemUtils';

/**
 * Check if the mailboxes are supported for Move/Copy cross account
 * Covers the following scenarios:
 * - From PST to PST
 * - From online account to PST file
 * - From PST file to online account
 * - From online account to online account
 * @param sourceMailboxInfo Source mailbox info
 * @param destinationMailboxInfo Destination mailbox info
 */
export default function getMoveCopyCrossAccountType(
    sourceMailboxInfo: MailboxInfo,
    destinationMailboxInfo: MailboxInfo
): MoveCopyType {
    const isSourceMailboxPST = isLocalDataBackedMailboxInfo(sourceMailboxInfo);
    const isDestinationMailboxPST = isLocalDataBackedMailboxInfo(destinationMailboxInfo);

    // Scenario 1: From PST to PST - Checks if the feature is PST enabled
    if (isSourceMailboxPST && isDestinationMailboxPST) {
        // PST Flight have to be enabled for Global Account
        return isAccountFeatureEnabled(
            'acct-pstFileImportExport',
            getGlobalSettingsAccountMailboxInfo()
        )
            ? 3
            : 0;
    }

    /* Scenario 2: From online account to PST file
       Checks:
        - If the OutlookDataFile property is different than deny or noGrow for sourceMailboxInfo
        - If the PST flight is enabled for sourceMailboxInfo
    */
    if (!isSourceMailboxPST && isDestinationMailboxPST) {
        const outlookDataFilePolicy =
            getAccountScopeUserSettings(sourceMailboxInfo)?.PolicySettings?.OutlookDataFile;

        const isOutlookDataFileMoveAllowed = isFeatureEnabled(
            'acct-win32acct-win32PolicyBlockingPst'
        )
            ? !getWin32Policy().DisablePst &&
              !getWin32Policy().PSTDisableGrow &&
              (outlookDataFilePolicy === 0 ||
                  outlookDataFilePolicy === 1 ||
                  outlookDataFilePolicy === 3)
            : outlookDataFilePolicy === 0 ||
              outlookDataFilePolicy === 1 ||
              outlookDataFilePolicy === 3;

        return isOutlookDataFileMoveAllowed &&
            isAccountFeatureEnabled('acct-pstFileImportExport', sourceMailboxInfo)
            ? 3
            : 0;
    }

    /* Scenario 3: From PST file to online account
        Checks:
         - If the OutlookDataFile property is different than deny for destinationMailboxInfo
         - If the PST flight is enabled for destinationMailboxInfo
    */
    if (isSourceMailboxPST && !isDestinationMailboxPST) {
        const isOutlookDataFileMoveAllowed = isFeatureEnabled(
            'acct-win32acct-win32PolicyBlockingPst'
        )
            ? !getWin32Policy().DisablePst &&
              getAccountScopeUserSettings(destinationMailboxInfo)?.PolicySettings
                  ?.OutlookDataFile !== 5
            : getAccountScopeUserSettings(destinationMailboxInfo)?.PolicySettings
                  ?.OutlookDataFile !== 5;

        return isOutlookDataFileMoveAllowed &&
            isAccountFeatureEnabled('acct-pstFileImportExport', destinationMailboxInfo)
            ? 3
            : 0;
    }

    // We enable cross account move/copy for consumer accounts regardless of the policy settings.
    const isPolicyEnabledForSourceMailbox =
        isConsumer(undefined, sourceMailboxInfo) ||
        getAccountScopeUserSettings(sourceMailboxInfo)?.PolicySettings?.ItemsToOtherAccountsEnabled;
    const isPolicyEnabledForDestinationMailbox =
        isConsumer(undefined, destinationMailboxInfo) ||
        getAccountScopeUserSettings(destinationMailboxInfo)?.PolicySettings
            ?.ItemsToOtherAccountsEnabled;

    // If we are in Gulp or Branching, we allow cross account move/copy without checking policies
    // so we can test with our mailboxes (Microsoft accounts)
    const satisfyPolicySettings =
        isGulpOrBranching() ||
        (isPolicyEnabledForSourceMailbox && isPolicyEnabledForDestinationMailbox);

    if (!satisfyPolicySettings) {
        // If policies are not satisfied, we return NotAllowedPolicy
        return 4;
    }

    // Scenario 4: From online account to online account. Features and policies are checked for both mailboxes
    if (
        isAccountFeatureEnabled('mon-move-copy-crossAccount', sourceMailboxInfo) &&
        isAccountFeatureEnabled('mon-move-copy-crossAccount', destinationMailboxInfo)
    ) {
        return 3;
    }
    return 0;
}
